






























































import { Component, Vue, Watch } from 'vue-property-decorator';
import AddQuestionPreview from '@/components/add-question/AddQuestionPreview.vue';
import AddQuestionBuild from '@/components/add-question/AddQuestionBuild.vue';
import AddQuestionLoading from '@/components/add-question/AddQuestionLoading.vue';
import AddQuestionInfo from '@/components/add-question/AddQuestionInfo.vue';
import AddQuestionUpload from '@/components/add-question/AddQuestionUpload.vue';
import QuestionStore, {
  QuestionStoreInterface,
  SaveQuestionEvent,
} from '@/resource/question/question.store';
import {
  BuildQuestion,
  extractBuildQuestion,
  getAllSubjects,
} from '@/utils/add-question/add-question-util';
import {
  defaultObjectiveBuildQuestion,
  defaultSubjectiveBuildQuestion,
} from '@/utils/add-question/add-question-constant';
import { getRole } from '@/utils/cookie';
import { UserRole } from '@/resource/user/user.model';
import { Subject } from '@/resource/subject/subject.model';
import { QuestionSubjective } from '@/resource/question/question.model';

@Component({
  components: {
    AddQuestionPreview,
    AddQuestionBuild,
    AddQuestionLoading,
    AddQuestionInfo,
    AddQuestionUpload,
  },
})
export default class AddQuestion extends Vue {
  questionStore: QuestionStoreInterface = QuestionStore;
  isLoading = false;

  buildQuestion: BuildQuestion | null = null;
  availSubjects: Subject[] = [];

  showDebug = false;

  isNewEditor = false;

  get userRole(): UserRole | null {
    return getRole();
  }

  get isSuperAdmin() {
    return this.userRole === UserRole.SuperAdmin ? true : false;
  }

  get initialQuestionId(): number {
    let questionId = 0;
    if (typeof this.$route.query['question-id'] === 'string') {
      const queryId = Number(this.$route.query['question-id']);

      if (!isNaN(queryId)) {
        questionId = queryId;
      }
    }
    return questionId;
  }

  get initialIsSubjective(): boolean {
    let isSubjective = false;
    if (typeof this.$route.query['is-subjective'] === 'string') {
      const queryIsSubjective = this.$route.query['is-subjective'];

      if (queryIsSubjective) {
        if (queryIsSubjective.toLowerCase() === 'true') {
          isSubjective = true;
        } else if (queryIsSubjective.toLowerCase() === 'false') {
          isSubjective = false;
        }
      }
    }
    return isSubjective;
  }

  get questionId() {
    return this.questionStore.questionId;
  }

  get question() {
    return this.questionStore.question;
  }

  get mathQuestion() {
    return this.questionStore.mathQuestion;
  }

  get isSubjective() {
    return this.questionStore.isSubjective;
  }

  get showPreview() {
    return !this.isLoading && this.mathQuestion;
  }

  get showBuild() {
    return !this.isLoading;
  }

  async created() {
    this.questionStore.changeQuestionId(this.initialQuestionId);
    this.questionStore.changeIsSubjective(this.initialIsSubjective);

    if (this.initialQuestionId) {
      await this.fetchQuestion();
    }

    this.updateBuildQuestion();
    await this.fetchSubjects();
    await this.previewQuestion();
  }

  async fetchSubjects() {
    this.isLoading = true;
    this.availSubjects = await getAllSubjects();
    this.isLoading = false;
  }

  async updateQuestionId(id: number) {
    this.questionStore.changeQuestionId(id);
    await this.fetchQuestion();
  }

  async updateIsSubjective(isSubjective: boolean) {
    this.questionStore.changeIsSubjective(isSubjective);
    await this.fetchQuestion();
  }

  async fetchQuestion() {
    if (this.questionId === 0) return;

    this.isLoading = true;
    await this.questionStore.fetchQuestion({ id: this.questionId });

    if (this.questionStore.error) {
      this.updateBuildQuestion();

      const error = `${this.questionStore.error.message}`;
      await this.previewQuestion();

      this.$toasted.global.mainToast({
        message: `${error}`,
      });
    }
    this.isLoading = false;
  }

  async previewQuestion() {
    if (!this.buildQuestion) return;

    this.isLoading = true;
    await this.questionStore.previewQuestion({
      id: this.questionId,
      buildQuestion: this.buildQuestion,
    });
    this.isLoading = false;
  }

  async updateAction() {
    await this.saveQuestion(SaveQuestionEvent.Update);
  }

  async publishAction() {
    await this.saveQuestion(SaveQuestionEvent.Publish);
  }

  async unpublishAction() {
    await this.saveQuestion(SaveQuestionEvent.Unpublish);
  }

  async createDraftAction() {
    await this.saveQuestion(SaveQuestionEvent.CreateDaft);
  }

  async createPublishAction() {
    await this.saveQuestion(SaveQuestionEvent.CreatePublish);
  }

  async saveQuestion(event: SaveQuestionEvent) {
    if (!this.buildQuestion) return;

    this.isLoading = true;
    await this.questionStore.saveQuestion({
      id: this.questionId,
      buildQuestion: this.buildQuestion,
      event,
    });

    if (this.questionStore.error) {
      const error = `${this.questionStore.error.message}`;
      this.$toasted.global.mainToast({
        message: `${error}`,
      });
    }

    this.isLoading = false;
  }

  async deleteQuestion() {
    this.isLoading = true;

    if (this.questionId !== 0) {
      await this.questionStore.deleteQuestion({
        questionId: this.questionId,
        isSubjective: this.isSubjective,
      });
    }

    if (this.questionStore.error) {
      this.$toasted.global.mainToast({
        message: this.questionStore.error.message,
      });
      this.isLoading = false;
    } else {
      this.$router.go(0);
    }
  }

  updateBuildQuestion() {
    if (this.question && this.questionId !== 0) {
      this.buildQuestion = extractBuildQuestion(
        this.question,
        this.isSubjective,
      );

      if ((this.question as QuestionSubjective).questionJson) {
        this.isNewEditor = true;
      }
    } else {
      if (this.isSubjective) {
        this.buildQuestion = defaultSubjectiveBuildQuestion;
      } else {
        this.buildQuestion = defaultObjectiveBuildQuestion;
      }
    }
  }

  async toggleEditor() {
    this.isNewEditor = !this.isNewEditor;
  }

  unauthorizedUser() {
    if (
      this.questionStore.error &&
      this.questionStore.error.message === 'Forbidden'
    ) {
      this.$router.push({ name: 'home' });
    }
  }

  @Watch('question')
  changeQuestion() {
    this.updateBuildQuestion();
  }

  @Watch('isSubjective')
  async changeSubjective() {
    this.updateBuildQuestion();
    if (this.questionId === 0) await this.previewQuestion();
  }

  @Watch('questionStore.error')
  changeError() {
    this.unauthorizedUser();
  }
}
